import React from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { MultiSelectCheckboxDropdown } from "custom-components/shared/MultiSelectCheckboxDropdown";
import { DatePicker } from "components/shared/form/Datepicker";
import { TimePicker } from "components/shared/form/Timepicker";
import { AttachmentsActions } from "components/shared/AttachmentsActions";
import { EntityDynamicFieldsForm } from "components/shared/dynamic-fields";
import { DocumentKeyEnum } from "enum/DocumentKeyEnum";
import { EntityTypeEnum } from "enum/EntityTypeEnum";
import { Field, INPUT_CLS } from "../shared/WhsFormField";

export default function IncidentFormFields({
  type,
  setType,
  typeOptions,
  typeOption,
  handleCreateIncidentType,
  handleEditIncidentType,
  handleDeleteIncidentType,
  severity,
  setSeverity,
  severityOptions,
  severityOption,
  handleCreateSeverity,
  handleEditSeverity,
  handleDeleteSeverity,
  date,
  setDate,
  time,
  setTime,
  reportedBy,
  setReportedBy,
  internalUserOptions,
  witnesses,
  setWitnesses,
  allUserOptions,
  locationName,
  setLocationName,
  status,
  setStatus,
  statuses,
  description,
  setDescription,
  correctiveAction,
  setCorrectiveAction,
  uploadedFiles,
  setUploadedFiles,
  dynamicFieldsRef,
  companyId,
  loggedInUser,
  isSubmitting,
  onSubmit,
  onCancel,
  loadingDropdowns,
}) {
  return (
    <form onSubmit={onSubmit} noValidate className="space-y-4 font-sans">
      <div className="grid grid-cols-1 gap-x-4 gap-y-3.5 sm:grid-cols-2">
        {/* Incident Type */}
        <Field label="Incident Type">
          <CustomCreatableDropdown
            options={typeOptions}
            value={typeOption}
            onChange={(newValue) => setType(newValue ? newValue.value : "")}
            onCreateOption={handleCreateIncidentType}
            onEditOption={handleEditIncidentType}
            onDeleteOption={handleDeleteIncidentType}
            addNewText="+ Add Incident Type"
            placeholder="Search or select incident type..."
            isLoading={loadingDropdowns}
            menuHeader="Select Incident Type"
          />
        </Field>

        {/* Severity */}
        <Field label="Severity">
          <CustomCreatableDropdown
            options={severityOptions}
            value={severityOption}
            onChange={(newValue) => setSeverity(newValue ? newValue.value : "")}
            onCreateOption={handleCreateSeverity}
            onEditOption={handleEditSeverity}
            onDeleteOption={handleDeleteSeverity}
            addNewText="+ Add Severity Level"
            placeholder="Search or select severity..."
            isLoading={loadingDropdowns}
            menuHeader="Select Severity"
          />
        </Field>

        {/* Incident Date */}
        <Field label="Incident Date">
          <DatePicker
            value={date ? new Date(date + "T00:00:00") : ""}
            onChange={([selectedDate]) => {
              if (selectedDate) {
                const y = selectedDate.getFullYear();
                const m = String(selectedDate.getMonth() + 1).padStart(2, "0");
                const d = String(selectedDate.getDate()).padStart(2, "0");
                setDate(`${y}-${m}-${dayFormat(d)}`);
              } else {
                setDate("");
              }
            }}
            className="text-xs font-semibold"
            placeholder="Choose incident date"
            options={{
              dateFormat: "d/m/Y",
              disableMobile: true,
              maxDate: "today",
            }}
          />
        </Field>

        {/* Incident Time */}
        <Field label="Incident Time">
          <TimePicker
            value={time}
            onChange={(e) => setTime(e.target.value)}
            placeholder="Choose incident time"
          />
        </Field>

        {/* Reported By */}
        <Field label="Reported By">
          <MultiSelectCheckboxDropdown
            options={internalUserOptions}
            value={reportedBy}
            onChange={(val) => setReportedBy(val)}
            placeholder="Select or enter reporter..."
            isMulti={false}
            allowCustom={true}
            usePortal={false}
          />
        </Field>

        {/* Witnesses */}
        <Field label="Witnesses">
          <MultiSelectCheckboxDropdown
            options={allUserOptions}
            value={witnesses}
            onChange={(val) => setWitnesses(val)}
            placeholder="Select or enter witnesses..."
            allowCustom={true}
            usePortal={false}
          />
        </Field>

        {/* Location */}
        <Field label="Location on Site">
          <input
            type="text"
            placeholder="e.g. Level 3 – Scaffolding Area"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            className={INPUT_CLS}
          />
        </Field>

        {/* Status */}
        <Field label="Status">
          <MultiSelectCheckboxDropdown
            options={statuses}
            value={status}
            onChange={(val) => setStatus(val)}
            placeholder="Select status..."
            isMulti={false}
            usePortal={false}
          />
        </Field>
      </div>

      {/* Description */}
      <Field label="Description">
        <textarea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Describe what occurred in detail..."
          className={`${INPUT_CLS} resize-none`}
        />
      </Field>

      {/* Immediate Action Taken */}
      <Field label="Immediate Action Taken">
        <textarea
          rows={2}
          value={correctiveAction}
          onChange={(e) => setCorrectiveAction(e.target.value)}
          placeholder="Describe actions taken immediately after the incident..."
          className={`${INPUT_CLS} resize-none`}
        />
      </Field>

      {/* Upload Incident Attachments */}
      <div className="pt-1">
        <Field label="Upload Incident Attachments / Evidence">
          <AttachmentsActions
            showLabel={false}
            files={uploadedFiles}
            onChange={setUploadedFiles}
            downloadKey={DocumentKeyEnum.IncidentReport}
          />
        </Field>
      </div>

      {/* Dynamic Custom Fields */}
      <EntityDynamicFieldsForm
        ref={dynamicFieldsRef}
        entityTypeCode={EntityTypeEnum.IncidentReport}
        companyId={companyId}
        userId={localStorage.getItem("whs_user_id") || loggedInUser?.id}
      />

      {/* Form Footer Action Buttons */}
      <div className="pt-3 border-t border-slate-200/70 flex gap-2.5">
        <button
          type="button"
          onClick={onCancel}
          disabled={isSubmitting}
          className="flex-1 py-2.5 sm:py-3 rounded-xl border border-slate-200 text-xs font-bold text-slate-700 hover:bg-slate-50 transition-colors cursor-pointer disabled:opacity-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-2 py-2.5 sm:py-3 px-4 rounded-xl bg-primary-600 hover:bg-primary-700 text-white font-bold text-xs shadow-md transition-all duration-200 disabled:opacity-60 flex items-center justify-center gap-2 cursor-pointer active:scale-[0.98]"
        >
          {isSubmitting ? (
            <>
              <ArrowPathIcon className="w-4 h-4 animate-spin text-white" />
              <span>Submitting Incident...</span>
            </>
          ) : (
            <span>Submit Incident Report</span>
          )}
        </button>
      </div>
    </form>
  );
}

function dayFormat(d) {
  return String(d).padStart(2, "0");
}
